import { useState, useEffect, useRef, useMemo } from 'react'
import {
  ConfigurableProduct,
  ConfigurableVariant,
  Maybe,
  useAddToCartMutation,
  useCartPageQuery,
  useFetchCartHeaderQuery,
} from '@magentoTypes'
import { Image } from '@next/image'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useTranslation } from 'next-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '~/elements/Button/Button'
import { Headline } from '~/elements/Headline/Headline'
import { Link } from '~/elements/Link/Link'
import { createMagentoClient } from '~/graphql/magentoClient'
import { useMedia } from '~/hooks/useMedia'
import { Benefits } from './Benefits'
import { SelectorButton } from './SelectorButton'
import { useRouter } from 'next/router'
import { useProductAttributes } from '~/hooks/useProductAttributes'
import { Price } from './Price'
import { animated, useTransition } from '@react-spring/web'
import { ProductSelector } from './ProductSelector'
import { getCartId, useCartId } from '~/hooks/useCartId'
import { CartPreview } from './CartPreview'
import { AddToWishlist } from '~/elements/AddToWishlist/AddToWishlist'
import { useBanderoleQuery, useNavigationQuery } from '@contentfulTypes'
import { usePreviewMode } from '~/hooks/usePreviewMode'
import { createGraphQLClient } from '~/graphql/contentfulClient'
import { CART_ID, DEFAULT_LOCALE } from 'config/constants'
import { DataLayerEnum, useGTM } from '~/hooks/useGTM'
import { PulseLoader } from 'react-spinners'
import { useScrollLock } from '~/lib/scrollLock'
import { SampleBoxSelector } from './SampleBoxSelector'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'
import { getSession } from 'next-auth/react'
import { Icon } from '~/elements/Icons/Icon'
import { Promo } from './Promo'
import { ContentfulButton } from '~/elements/Button/ContentfulButton'
import ProductCardIcon from '~/modules/PLP/components/Icons/ProductCardIcon'
import { ProductRating } from '~/modules/ClientReviews/components/ProductRating'
import { ButtonMain } from '~/elements/Button/ButtonMain'
import { getCioProductPrice } from '~/lib/cioHelpers'
import { checkIsSample } from '~/lib/getProductFeature'
import { useProductTracking } from '~/lib/tracking/useProductTracking'
import { WishlistEventTypeEnum } from '~/lib/tracking/types/WishlistEventTypeEnum'

type Props = {
  className?: string
  product: ConfigurableProduct
  productPrice: ConfigurableProduct
  rating?: number
  reviews?: number
}

export const Sidebar = ({ className, product, productPrice }: Props) => {
  const [showProductSelector, setShowProductSelector] = useState(false)
  const [showSampleBoxSelector, setShowSampleBoxSelector] = useState(false)
  const [showCart, setShowCart] = useState(false)
  const [sizeError, setSizeError] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)

  const [windowHeight, setWindowHeight] = useState<number | null>(null)
  const [routeChanging, setRouteChanging] = useState(false)
  const stickyContainerRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()
  const { dataLayer } = useGTM()
  const { triggerProductEvent } = useProductTracking()
  const router = useRouter()
  const { findLabel } = useProductAttributes()
  const isDesktop = useMedia('lg')
  const queryClient = useQueryClient()
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE
  const mutationClient = createMagentoClient({ queryClient, usePost: true, locale: finalLocale })
  const isOneSize = product?.variants?.length === 1
  const isOutOfStock = productPrice?.stock_status === 'OUT_OF_STOCK'
  const [createCartSession, setCreateCartSession] = useState(false)
  const [cartId] = useCartId(createCartSession)
  const selectedSize = isOneSize
    ? product?.variants?.[0]?.attributes?.[0]?.value_index
    : router?.query?.selected_size
  const addToCart = useAddToCartMutation(mutationClient, {
    onSettled: async (data, error) => {
      if (data) {
        await queryClient.refetchQueries(useCartPageQuery.getKey({ cartId }))
        await queryClient.invalidateQueries(useFetchCartHeaderQuery.getKey({ cartId }))
        setShowCart(true)
      } else {
        toast.error(error?.message)
      }
    },
    onError: async () => {
      await queryClient.invalidateQueries([CART_ID])
      await queryClient.invalidateQueries(['session'])
    },
  })
  const preview = usePreviewMode()
  const contentfulClient = createGraphQLClient({ preview })
  const { data: navigationData } = useNavigationQuery(contentfulClient, {
    preview,
    locale: finalLocale,
  })
  const { isFetched: banderoleFeteched, data: banderoleData } = useBanderoleQuery(
    contentfulClient,
    {
      preview,
      locale: finalLocale,
    },
  )

  useEffect(() => {
    if (router.isReady && !isHydrated) setIsHydrated(true)
  }, [router.isReady, isHydrated])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      function handleResize() {
        if (!isDesktop) return
        setWindowHeight(window.innerHeight)
      }
      window.addEventListener('resize', handleResize)
      handleResize()
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [isDesktop])

  useEffect(() => {
    const handleRouteStart = () => {
      setRouteChanging(true)
    }
    const handleRouteComplete = () => {
      setRouteChanging(false)
    }

    router.events.on('routeChangeStart', handleRouteStart)
    router.events.on('routeChangeComplete', handleRouteComplete)

    return () => {
      router.events.off('routeChangeStart', handleRouteStart)
      router.events.off('routeChangeComplete', handleRouteComplete)
    }
  }, [router.events])

  const selectedProduct: Maybe<ConfigurableVariant> | undefined = product?.variants?.find(
    (variant) => variant?.attributes?.[0]?.value_index === Number(selectedSize),
  )

  const selectedProductPrice: Maybe<ConfigurableVariant> | undefined = productPrice?.variants?.find(
    (variant) => variant?.attributes?.[0]?.value_index === Number(selectedSize),
  )

  const sampleProduct = product?.variants?.find(
    (variant) =>
      variant?.attributes?.[0]?.value_index === 11932 ||
      variant?.attributes?.[0]?.value_index === 10771,
  )
  const sampleOutOfStock = sampleProduct?.product?.stock_status === 'OUT_OF_STOCK'
  const selectedSizeLabel = selectedProduct?.attributes?.[0]?.label

  const prepareAddToCart = async () => {
    if (!createCartSession) {
      setCreateCartSession(true)
      await new Promise((resolve) => {
        const checkCartId = setInterval(() => {
          const awaitingCartId = getCartId()
          if (awaitingCartId) {
            clearInterval(checkCartId)
            resolve(null)
          }
        }, 500)
      })
    }
    await queryClient.invalidateQueries([CART_ID])
    await queryClient.invalidateQueries(['session'])
  }

  const addProductToCart = async () => {
    const session = await getSession()
    if (!selectedSize && !isOneSize) {
      setSizeError(true)
      return
    }
    if (!cartId && !product?.sku) return
    const selectedUid = selectedProduct?.attributes?.[0]?.uid
    if (selectedUid) {
      const priceRange = selectedProductPrice
        ? selectedProductPrice?.product?.price_range
        : productPrice?.price_range
      const price =
        priceRange?.minimum_price?.final_price?.value !==
        priceRange?.minimum_price?.regular_price?.value
          ? priceRange?.minimum_price?.final_price
          : priceRange?.minimum_price?.regular_price
      dataLayer(DataLayerEnum.EVENT, 'add_to_cart', {
        ecommerce: {
          currency: price?.currency || 'EUR',
          items: [
            {
              item_id: selectedProduct ? selectedProduct?.product?.sku : product?.sku,
              item_ean: selectedProduct ? selectedProduct?.product?.eannummer : product?.eannummer,
              item_name: product?.name,
              item_variant: selectedProduct?.attributes?.[0]?.label as string,
              item_brand: findLabel({
                attributeCode: 'manufacturer',
                attributeValue: product?.manufacturer as number,
              }),
              item_category: '',
              price: price?.value,
              quantity: 1,
            },
          ],
        },
      })
      addToCart.mutate({
        cartId: session ? (cartId as string) : (getCartId() as string),
        cartItems: [
          {
            quantity: 1,
            sku: product?.sku as string,
            selected_options: [selectedUid],
          },
        ],
      })
    }
  }

  const slideIn = {
    from: { backgroundColor: 'rgba(20,20,20,0)', y: 100 },
    enter: { backgroundColor: 'rgba(20,20,20,0.6)', y: 0 },
    leave: { backgroundColor: 'rgba(20,20,20,0)', y: 100 },
  }
  const selectorTransitions = useTransition(showProductSelector, slideIn)
  const sampleBoxTransitions = useTransition(showSampleBoxSelector, slideIn)
  const cartTransitions = useTransition(showCart, slideIn)

  const availableShapes = () => {
    if (!product) return 0
    const related = product?.related_products?.map((related) => related?.benuta_form_new)
    related?.push(product?.benuta_form_new)
    return new Set(related)?.size
  }

  const { lockScroll, unlockScroll } = useScrollLock()

  const openSelector = async () => {
    router.query['url_key'] = product?.url_key as string
    await router.push(router, undefined, { shallow: true })
    setShowProductSelector(true)
    lockScroll()
    document?.getElementById('sidebar-close-button')?.focus()
  }
  const closeSelector = async () => {
    delete router.query['url_key']
    await router.push(router, undefined, { shallow: true })
    setShowProductSelector(false)
    unlockScroll()
  }
  const openSampleBoxSelector = () => {
    setShowSampleBoxSelector(true)
  }
  const closeSampleBoxSelector = () => {
    setShowSampleBoxSelector(false)
  }
  useEffect(() => {
    if (closeButtonRef?.current) {
      closeButtonRef?.current?.focus()
    }
  }, [showProductSelector, showCart])

  const availableSizes = product?.variants?.length
  const stickyContainerHeight = stickyContainerRef?.current?.getBoundingClientRect().height
  const stickyOffset = () => {
    if (!windowHeight || !stickyContainerHeight) return 0
    if (stickyContainerHeight < windowHeight) return 0
    return windowHeight - stickyContainerHeight - 40
  }
  const onWishlistEvent = (eventName: WishlistEventTypeEnum) => {
    const price =
      productPrice.price_range?.minimum_price?.final_price?.value !==
      productPrice.price_range?.minimum_price?.regular_price?.value
        ? productPrice.price_range?.minimum_price?.final_price
        : productPrice.price_range?.minimum_price?.regular_price
    triggerProductEvent(eventName, product, price)
  }

  const cioVariationId = useMemo(() => {
    if (selectedProduct?.product?.sku && product?.sku) {
      return `${selectedProduct.product.sku}-c-${product.sku}`
    }

    const nonSampleVariants = product?.variants
      ?.filter((variant) => variant?.attributes?.[0]?.code === 'size')
      .filter((size) => !checkIsSample(size?.attributes?.[0]?.value_index as number))

    if (nonSampleVariants?.[0]?.product?.sku) {
      return `${nonSampleVariants[0].product.sku}-c-${product.sku}`
    }

    return undefined
  }, [selectedProduct, product])

  const cioProductPrice = useMemo(() => {
    return getCioProductPrice(
      selectedProductPrice?.product?.price_range || productPrice?.price_range,
    )
  }, [selectedProductPrice, productPrice])

  return (
    <>
      <aside
        className={clsx(
          'row-start-2 flex flex-col px-4 lg:col-start-2 lg:row-start-1 lg:row-end-4 lg:px-20',
          className,
        )}
        data-cnstrc-product-detail
        data-cnstrc-item-id={product?.sku}
        data-cnstrc-item-variation-id={cioVariationId}
        data-cnstrc-item-name={product?.name}
        data-cnstrc-item-price={cioProductPrice}
      >
        <div
          ref={stickyContainerRef}
          className={clsx('sticky')}
          style={
            isDesktop
              ? {
                  top: stickyOffset(),
                }
              : {}
          }
        >
          <Headline type="h1" styleAs="slider" className="mt-4 block w-full lg:mt-0">
            {product?.name}
          </Headline>
          <div className="mb-6 flex w-full items-center justify-between lg:mb-8">
            <a
              className="mt-2 shrink-0"
              id="productvariations"
              title={t('Product Description')}
              href="#recommendations"
              onClick={() => router.replace('#recommendations')}
            >
              <ProductRating product={product} withText />
            </a>
            <div className="min-h-[24px]">
              <AddToWishlist
                className="h-6 w-6 lg:h-8 lg:w-8"
                trackingHandler={onWishlistEvent}
                sku={product?.sku as string}
                withCioAttributes
              />
            </div>
          </div>
          <div className="mb-2 flex gap-4 font-medium lg:gap-0">
            {isHydrated && (
              <Price
                regPriceClass="text-[20px]"
                salePriceClass="text-[20px]"
                priceRange={
                  selectedProductPrice
                    ? selectedProductPrice?.product?.price_range
                    : productPrice?.price_range
                }
                showSaveAmount
              />
            )}
          </div>
          {isHydrated && (
            <p className="min-h-[20px] text-p-small font-extralight">
              {t('incl VAT, delivery time approx', {
                days: navigationData?.pageCollection?.items[0]?.deliveryDays,
              })}
            </p>
          )}
          {banderoleFeteched &&
            banderoleData?.pageCollection?.items[0]?.banderole?.countDown &&
            product?.categories?.findIndex(
              (v) =>
                v?.url_key ===
                banderoleData?.pageCollection?.items[0]?.banderole?.countDown?.pdpCategory?.replace(
                  '.html',
                  '',
                ),
            ) != -1 && <Promo className="my-6 lg:mt-6 lg:mb-0" />}
          {isHydrated && isOutOfStock ? (
            <strong className="lg:text-h3 block py-6 text-h3-mobile font-medium  text-primary-pink">
              {t('Out of stock')}
            </strong>
          ) : null}
          <menu className="mb-6  mt-8 grid grid-cols-5 gap-6 lg:mb-12">
            <li className="grid min-h-[60px] min-w-[52px] place-items-center border p-3">
              <Image
                src={product?.small_image?.url as string}
                alt={product?.small_image?.label as string}
                width={28}
                height={36}
                hasNoBlur
              />
            </li>
            {product?.related_products
              ?.filter((elem) => elem?.benuta_form_new === product?.benuta_form_new)
              ?.map((item) => (
                <li key={item?.url_key} className="block min-h-[60px] min-w-[52px]">
                  <Link className="block" href={`/${item?.url_key}.html`}>
                    <Image
                      src={item?.small_image?.url as string}
                      alt={item?.small_image?.label as string}
                      width={45}
                      height={60}
                      hasNoBlur
                    />
                  </Link>
                </li>
              ))}
          </menu>
          {sampleProduct && !sampleOutOfStock ? (
            <Button
              onClick={openSampleBoxSelector}
              ctaButtonStyle="MediumText"
              className="mb-3 w-full !text-left !no-underline"
            >
              <div className="flex items-center justify-start">
                <ProductCardIcon name="sample" />
                <span className="ml-3.5 !whitespace-nowrap underline !underline-offset-4">
                  {t('Unsure? Order a carpet sample')}
                </span>
              </div>
            </Button>
          ) : null}
          {isHydrated && (
            <>
              {isOneSize && availableShapes() < 2 ? null : (
                <SelectorButton
                  onClick={openSelector}
                  className="mb-2"
                  selectorText={t('Choose shape')}
                  totalOptionAmount={availableShapes()}
                  currentlyChosenOption={findLabel({
                    attributeCode: 'benuta_form_new',
                    attributeValue: product?.benuta_form_new ? product?.benuta_form_new : '',
                  })}
                />
              )}
              <SelectorButton
                onClick={openSelector}
                className="mb-2"
                selectorText={isOneSize ? t('Size') : t('Choose size')}
                totalOptionAmount={availableSizes}
                currentlyChosenOption={
                  isOneSize
                    ? (product?.variants?.[0]?.attributes?.[0]?.label as string)
                    : selectedSizeLabel
                    ? selectedSizeLabel
                    : ''
                }
              />

              {sizeError && !!!selectedSize && !isOneSize ? (
                <p className="p-1 text-center text-primary-pink">{t('Please select size')}</p>
              ) : null}
              <ButtonMain
                className="-mb-2 lg:mb-12"
                disabled={isOutOfStock}
                fullWidth
                onClick={async () => {
                  await prepareAddToCart()
                  addProductToCart()
                }}
                data-cnstrc-btn="add_to_cart"
              >
                {addToCart.isLoading ? (
                  <PulseLoader loading={true} size={6} color="#fff" />
                ) : (
                  t('Add to cart')
                )}
              </ButtonMain>
            </>
          )}
          <a
            title={t('Product Description')}
            href="#product-description"
            className="hidden lg:mb-12 lg:block"
            onClick={() => router.replace('#product-description')}
          >
            <Button ctaButtonStyle="MediumText" className="text-left !underline-offset-4">
              {t('View product details')}
            </Button>
          </a>
          <div className="mb-2 min-h-[120px] lg:mb-12">
            <Benefits />
          </div>
          {product?.benuta_ar && navigationData?.pageCollection?.items?.[0]?.arBox ? (
            <>
              <div className="my-8 box-content hidden border border-grey-border border-opacity-50 p-8 lg:block">
                <p>
                  <Icon className="mr-2 inline" name="augmented-reality-cube" />
                  <span>{navigationData?.pageCollection?.items?.[0]?.arBox?.headline}</span>
                </p>
                <p
                  className="my-2 text-h3-mobile"
                  dangerouslySetInnerHTML={{
                    __html: navigationData?.pageCollection?.items?.[0]?.arBox?.text as string,
                  }}
                />
                <ContentfulButton
                  {...navigationData?.pageCollection?.items?.[0]?.arBox?.cta}
                  className="!text-left"
                />
              </div>
            </>
          ) : null}
        </div>
      </aside>
      {selectorTransitions(
        (animatedStyles, item) =>
          item && (
            <animated.div className={clsx('fixed inset-0 z-50 w-full', 'vh100')}>
              <div className=" vh100 relative w-full lg:flex lg:justify-end">
                <animated.div
                  onClick={closeSelector}
                  className="absolute inset-0 transition-all duration-200 ease-in-out"
                  style={{
                    backgroundColor: routeChanging
                      ? 'rgba(255,255,255,0.6)'
                      : animatedStyles.backgroundColor,
                  }}
                ></animated.div>
                <animated.nav
                  style={{
                    transform: animatedStyles.y.to((value) =>
                      isDesktop
                        ? `translate3d(${value}%,0px,0px)`
                        : `translate3d(0px,${-value}%,0px)`,
                    ),
                  }}
                >
                  <button
                    ref={closeButtonRef}
                    id="sidebar-close-button"
                    className="absolute top-4 right-4 z-[60] grid h-10 w-10 place-content-center rounded-full bg-grey-light text-grey-dark lg:top-8 lg:right-8"
                    onClick={closeSelector}
                  >
                    <Image
                      src="/images/Icons/close.svg"
                      width={24}
                      height={24}
                      placeholder="empty"
                      alt="close-icon"
                    />
                  </button>
                  <ProductSelector
                    closeFlyout={closeSelector}
                    showProductSelector={showProductSelector}
                  />
                </animated.nav>
              </div>
            </animated.div>
          ),
      )}
      {sampleBoxTransitions(
        (animatedStyles, item) =>
          item && (
            <animated.div className={clsx('fixed inset-0 z-50 w-full', 'vh100')}>
              <div className=" vh100 relative w-full lg:flex lg:justify-end">
                <animated.div
                  onClick={closeSampleBoxSelector}
                  className="absolute inset-0 transition-all duration-200 ease-in-out"
                  style={{
                    backgroundColor: routeChanging
                      ? 'rgba(255,255,255,0.6)'
                      : animatedStyles.backgroundColor,
                  }}
                ></animated.div>
                <animated.nav
                  style={{
                    transform: animatedStyles.y.to((value) =>
                      isDesktop
                        ? `translate3d(${value}%,0px,0px)`
                        : `translate3d(0px,${-value}%,0px)`,
                    ),
                  }}
                >
                  <button
                    ref={closeButtonRef}
                    id="sidebar-close-button"
                    className="absolute top-4 right-4 z-[60] grid h-10 w-10 place-content-center rounded-full bg-grey-light text-grey-dark lg:top-8 lg:right-8"
                    onClick={closeSampleBoxSelector}
                  >
                    <Image
                      src="/images/Icons/close.svg"
                      width={24}
                      height={24}
                      placeholder="empty"
                      alt="close-icon"
                    />
                  </button>

                  <SampleBoxSelector
                    closeFlyout={closeSampleBoxSelector}
                    showSampleBoxSelector={showSampleBoxSelector}
                    urlKey={product?.url_key as string}
                  />
                </animated.nav>
              </div>
            </animated.div>
          ),
      )}
      {cartTransitions(
        (animatedStyles, item) =>
          item && (
            <animated.div className={clsx('fixed inset-0 z-50 w-full', 'vh100')}>
              <div className="vh100 relative w-full lg:flex lg:justify-end">
                <animated.div
                  onClick={() => setShowCart(false)}
                  className="absolute inset-0 transition-all duration-200 ease-in-out"
                  style={{ backgroundColor: animatedStyles.backgroundColor }}
                ></animated.div>

                <animated.div
                  style={{
                    transform: animatedStyles.y.to((value) =>
                      isDesktop ? `translate(${value}%,0px)` : `translate(0px,${-value}%)`,
                    ),
                    width: isDesktop ? '550px' : '100%',
                  }}
                  className="bg-primary-white"
                >
                  <button
                    ref={closeButtonRef}
                    className="absolute top-4 right-4 z-50 grid h-10 w-10 place-content-center rounded-full bg-grey-light text-grey-dark lg:top-8 lg:right-8"
                    onClick={() => setShowCart(false)}
                  >
                    <Image
                      src="/images/Icons/close.svg"
                      width={24}
                      height={24}
                      placeholder="empty"
                      alt="close-icon"
                    />
                  </button>
                  <CartPreview
                    addedProduct={{
                      sku: product?.sku,
                      sizeUid:
                        selectedProduct?.attributes?.[0]?.uid ??
                        sampleProduct?.attributes?.[0]?.uid,
                    }}
                    closeFlyout={() => setShowCart(false)}
                  />
                </animated.div>
              </div>
            </animated.div>
          ),
      )}
    </>
  )
}
